import type { GameFormVariant } from '@/components/GameFormVariant';
import type { IconVariant } from '@/components/Icon';

export interface CmsLink {
  id: string;
  path: string;
  workspace?: string;
}

export interface CmsAssetNotNull {
  id: string;
  path: string;
  name: string;
  title?: string;
  subject?: string;
  description?: string;
  endDate?: string;
  startDate?: string;
}

export type CmsAsset = CmsAssetNotNull | null;

export interface CmsNode {
  id: string;
  name?: string;
  path?: string;
  template?: string;
  nodes?: CmsComponent[] | CmsNode[] | CmsArea[];
  // CMS nodes carry arbitrary template-specific fields; narrowing to `unknown` here cascades
  // into ~20 new type errors across unrelated call sites that already assume typed access.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [prop: string]: any;
}

export interface CmsPage extends CmsNode {
  template: string;
  description?: string;
  queryParams?: { [key: string]: string | number };
  nodes?: CmsArea[];
}

export interface CmsArea extends CmsNode {
  nodes?: CmsComponent[];
}

export type CmsComponent = CmsNode;

export type CmsNavigationChildComponent =
  | CmsNavigationGroupComponent
  | CmsNavigationLinkComponent;

export interface CmsNavigationAreaComponent extends CmsComponent {
  nodes: CmsNavigationRootComponent[];
}

export interface CmsNavigationRootComponent extends CmsComponent {
  title: string;
  shortTitle?: string;
  theme: '0' | '1' | '2' | '3' | '4' | '11';
  game?: CmsLink;
  overview?: CmsLink;
}

export interface CmsNavigationLinkComponent extends CmsComponent {
  title: string;
  shortTitle?: string;
  link: CmsLink;
  icon?: IconVariant;
}

export interface CmsNavigationGroupComponent extends CmsComponent {
  title: string;
  shortTitle?: string;
  icon?: IconVariant;
}

export interface CmsArticleChunk {
  index: number;
  isLastChunk: boolean;
  articles: CmsArticleItem[];
  size: number;
}

export interface CmsRecentArticles {
  [category: string]: CmsArticleItem[];
}

export interface CmsArticleItem {
  category: string;
  datetime: number;
  path: string;
  title: string;
  date: string;
  teaser?: string;
  image?: CmsAsset | string;
}

export interface CmsSubsidiary extends CmsNode {
  name: string;
  image: CmsAsset;
  state: string;
  telephone: string;
  fax?: string;
  email: string;
  website: string;
  address: string;
  authority?: string;
  pressVisdp?: string;
  pressName?: string;
  pressTelephone?: string;
  pressFax?: string;
  pressEmail?: string;
  pressWebsite?: string;
  pressAddress?: string;
  fees: string;
  lottoClosingDateWednesday: string;
  lottoClosingDateSaturday: string;
  eurojackpotClosingDate: string;
  eurojackpotClosingDateTuesday?: string; // TODO change to required in future https://jira.tln-hannover.de/browse/LOTREL-572
  gluecksspiraleClosingDate: string;
  kenoClosingDate: string;
  totoAuswahlClosingDateSaturday: string;
  totoErgebnisClosingDateSaturday: string;
}

export interface CmsFooterAreaComponent extends CmsComponent {
  nodes?: CmsFooterLink[];
}

export interface CmsFooterLink extends CmsComponent {
  title: string;
  link?: CmsLink;
}

export interface CmsSitemapItem {
  title: string;
  path: string;
  children?: CmsSitemapItem[];
}

export interface CmsEventTeaser {
  title: string;
  link: string;
  linkTitle: string;
  imageDesktop: string;
  imageMobile: string;
}

export interface CmsAccountPage {
  path: string;
  variant: GameFormVariant;
}

export enum CmsGamePageVariant {
  Standard = 'standard',
  External = 'external',
  IFrame = 'iframe',
}

export interface CmsGamePage {
  title: string;
  game: string;
  path: string;
  variant: CmsGamePageVariant;
}
